import { useState, type FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/db6co14be24agvuu3i90';

type Status = 'idle' | 'submitting' | 'success' | 'error';

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      return;
    }
    data.delete('website_alt');

    setStatus('submitting');
    setFormError('');

    const params = new URLSearchParams();
    data.forEach((value, key) => {
      params.append(key, String(value));
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = serverMsg.toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        setFormError('');
        form.reset();
        return;
      }

      setStatus('error');
      setFormError(serverMsg || '提交失败，请稍后重试。');
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试。');
    }
  };

  return (
    <form
      id="about-contact-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="space-y-4"
    >
      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="field-website-alt"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="contact-name" className="block text-xs text-foreground-600 mb-1.5">
            您的称呼
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            placeholder="请输入姓名或昵称"
            className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-xs text-foreground-600 mb-1.5">
            电子邮箱
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="name@example.com"
            className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
          />
        </div>
      </div>

      <div>
        <label htmlFor="contact-subject" className="block text-xs text-foreground-600 mb-1.5">
          留言主题
        </label>
        <select
          id="contact-subject"
          name="subject"
          defaultValue="内容建议"
          className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 cursor-pointer"
        >
          <option value="内容建议">内容建议</option>
          <option value="剧情讨论">剧情讨论</option>
          <option value="合作咨询">合作咨询</option>
          <option value="版权反馈">版权反馈</option>
          <option value="其他">其他</option>
        </select>
      </div>

      <div>
        <label htmlFor="contact-message" className="block text-xs text-foreground-600 mb-1.5">
          留言内容
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={4}
          placeholder="请输入你的留言（500字以内）"
          className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 resize-none"
        ></textarea>
      </div>

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="w-full sm:w-auto px-6 py-2.5 rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {status === 'submitting' ? '提交中...' : '提交留言'}
      </button>

      {status === 'success' && (
        <p className="flex items-center gap-1.5 text-sm text-primary-700 bg-primary-100 rounded-md px-3 py-2.5">
          <i className="ri-checkbox-circle-line"></i>
          留言已提交，感谢你的反馈，我们会尽快查看！
        </p>
      )}
      {status === 'error' && (
        <p className="flex items-center gap-1.5 text-sm text-accent-800 bg-accent-100 rounded-md px-3 py-2.5">
          <i className="ri-error-warning-line"></i>
          {formError}
        </p>
      )}
    </form>
  );
}